<script setup lang="ts">
import { getLabelByFrequentie } from '@alert-lib/alertOptions';
import { useClaims } from '@auth/useClaims';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import {
  getStandaardAlertsGetByAdviseurQueryKey,
  standaardAlertsGetByAdviseur,
  useStandaardAlertsDelete,
} from '@services/alerts-public/orval-api/standaard-alerts/standaard-alerts';
import { adviseurApi } from '@services/crm-internal/service';
import { VButton } from '@shared/component/VButton';
import VChip from '@shared/component/VChip/VChip.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import SelectField from '@shared/form/SelectField/SelectField.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, ref, unref, watch } from 'vue';
import StandaardAlertForm from './StandaardAlertForm.vue';

const queryClient = useQueryClient();
const standaardAlertId = ref<string>();
const showStandaardAlertForm = ref(false);
const notification = useNotification();
const internAdviseurnummer = 1;
const claims = useClaims();

const adviseurnummer = ref<number>();

watch(
  () => claims.value?.isInternal,
  (v) => {
    if (v) adviseurnummer.value = internAdviseurnummer;
  },
);

const { data: adviseurs, error: adviseursError } = useQuery({
  queryKey: ['adviseurData'],
  queryFn: () => adviseurApi.tussenpersonenGet(),
  select: (result) => result.data?.result?.filter((adviseur) => adviseur.id > 0),
  enabled: computed(() => claims.value?.isInternal ?? false),
});

const geselecteerdeAdviseur = computed(() =>
  adviseurs?.value?.find((x) => x.id === adviseurnummer.value),
);

const queryKey = computed(() =>
  getStandaardAlertsGetByAdviseurQueryKey({
    adviseurnummer: adviseurnummer.value,
  }),
);

useWatchErrorHandler(adviseursError);

const { data: standaardalerts, isPending } = useQuery({
  queryKey: [queryKey],
  queryFn: () => standaardAlertsGetByAdviseur({ adviseurnummer: adviseurnummer.value }),
  select: (result) => result?.result ?? [],
});
useWatchErrorHandler(adviseursError);

const { mutate: removeStandaardAlert, isPending: isRemovingStandaardAlert } =
  useStandaardAlertsDelete({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: unref(queryKey) }),
      onError: (e: any) => notification.error(e.data?.title ?? 'Onbekende Fout'),
    },
  });

function showForm(id: string | undefined = undefined) {
  standaardAlertId.value = id;
  showStandaardAlertForm.value = true;
}
</script>

<template>
  <div>
    <div v-if="isPending">
      <VSkeleton height="lg" :count="1" />
      <hr />
      <VSkeleton height="lg" :count="4" />
    </div>
    <div v-else>
      <div class="flex flex-col">
        <div class="mb-6 flex flex-row justify-between">
          <div v-if="claims?.isInternal" class="max-w-lg flex-1">
            <SelectField
              :options="
                adviseurs?.map((adviseur) => ({
                  value: adviseur.id,
                  label: `${adviseur.name} (${adviseur.id})`,
                }))
              "
              v-model:value="adviseurnummer"
              placeholder="Selecteer adviseur (interne optie)"
            />
          </div>

          <VButton prepend-icon="fas fa-plus" @click="showForm()"> Alert toevoegen </VButton>
        </div>

        <VDataTable
          :value="standaardalerts"
          @row-click="(alert) => showForm(alert.data.id)"
          show-row-arrow
        >
          <VColumn header="Omschrijving" field="titel" />
          <VColumn header="Frequentie" field="frequentie">
            <template #body="{ data }">
              {{ getLabelByFrequentie(data.frequentie) }}
            </template>
          </VColumn>
          <VColumn header="Ontvangers" field="ontvangers">
            <template #body="{ data }">
              <div class="flex gap-2">
                <VChip :label="ontvanger" v-for="ontvanger in data.ontvangers" :key="ontvanger" />
              </div>
            </template>
          </VColumn>
          <VColumn field="verwijderen" align="right">
            <template #body="{ data }">
              <VButton
                inverted
                type="danger"
                confirm-dialog
                confirm-dialog-text="Weet je zeker dat je deze standaard alert wil verwijderen?"
                confirm-dialog-button-text="Verwijderen"
                prepend-icon="far fa-trash-alt"
                :loading="isRemovingStandaardAlert"
                @click="() => removeStandaardAlert({ standaardAlertId: data.id })"
              />
            </template>
          </VColumn>
        </VDataTable>
      </div>
    </div>
    <StandaardAlertForm
      @formClosed="showStandaardAlertForm = false"
      :standaardalert-id="standaardAlertId"
      :geselecteerde-adviseur="geselecteerdeAdviseur"
      v-if="showStandaardAlertForm"
    />
  </div>
</template>
